import { isValidCpf, onlyDigits } from '@/lib/masks'
import type { IdentityDocType, IdentityDocument } from './api'

/**
 * O documento digitado, como o formulário guarda e como a API recebe (RN-PAC-50, RN-POR-19): o CPF,
 * ou, com "Não tenho CPF", o documento estrangeiro com tipo, número e país. As mensagens são as da API.
 */

export interface DocumentValues {
  docNumber: string
  hasNoCpf: boolean
  foreignType: Exclude<IdentityDocType, 'cpf'>
  foreignNumber: string
  foreignCountry: string
}

export const EMPTY_DOCUMENT: DocumentValues = {
  docNumber: '',
  hasNoCpf: false,
  foreignType: 'passport',
  foreignNumber: '',
  foreignCountry: '',
}

export const DOCUMENT_MESSAGES = {
  cpf: 'O CPF precisa de 11 dígitos.',
  cpfInvalid: 'Esse CPF não é válido. Confira os dois últimos dígitos.',
  foreignNumber: 'O documento pode ter de 4 a 20 letras e números.',
  foreignCountry: 'Escolha o país que emitiu o documento.',
} as const

export type DocumentErrors = Partial<
  Record<'docNumber' | 'foreignNumber' | 'foreignCountry', string>
>

export function validateDocument(values: DocumentValues): DocumentErrors {
  if (values.hasNoCpf) {
    const errors: DocumentErrors = {}
    if (!/^[A-Za-z0-9]{4,20}$/.test(values.foreignNumber.trim())) {
      errors.foreignNumber = DOCUMENT_MESSAGES.foreignNumber
    }
    if (!values.foreignCountry) errors.foreignCountry = DOCUMENT_MESSAGES.foreignCountry
    return errors
  }
  const digits = onlyDigits(values.docNumber)
  if (digits.length !== 11) return { docNumber: DOCUMENT_MESSAGES.cpf }
  if (!isValidCpf(digits)) return { docNumber: DOCUMENT_MESSAGES.cpfInvalid }
  return {}
}

/** O corpo da API, ou nulo quando o documento não está inteiro. */
export function toIdentityDocument(values: DocumentValues): IdentityDocument | null {
  if (Object.keys(validateDocument(values)).length > 0) return null
  return values.hasNoCpf
    ? {
        type: values.foreignType,
        number: values.foreignNumber.trim().toUpperCase(),
        country: values.foreignCountry,
      }
    : { type: 'cpf', number: onlyDigits(values.docNumber) }
}

/** O mesmo documento? (para saber se a confirmação ainda vale depois de a pessoa mexer no campo) */
export function documentKey(document: IdentityDocument | null): string {
  if (!document) return ''
  return document.type === 'cpf'
    ? `cpf:${document.number}`
    : `${document.type}:${document.country ?? ''}:${document.number}`
}
